import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

// Country codes with flag, dial code, and validation
const COUNTRIES = [
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹', maxDigits: 9 },
  { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷', maxDigits: 11 },
  { code: 'ES', name: 'Espanha', dialCode: '+34', flag: '🇪🇸', maxDigits: 9 },
  { code: 'FR', name: 'França', dialCode: '+33', flag: '🇫🇷', maxDigits: 9 },
  { code: 'DE', name: 'Alemanha', dialCode: '+49', flag: '🇩🇪', maxDigits: 11 },
  { code: 'GB', name: 'Reino Unido', dialCode: '+44', flag: '🇬🇧', maxDigits: 10 },
  { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸', maxDigits: 10 },
  { code: 'IT', name: 'Itália', dialCode: '+39', flag: '🇮🇹', maxDigits: 10 },
  { code: 'NL', name: 'Holanda', dialCode: '+31', flag: '🇳🇱', maxDigits: 9 },
  { code: 'BE', name: 'Bélgica', dialCode: '+32', flag: '🇧🇪', maxDigits: 9 },
  { code: 'CH', name: 'Suíça', dialCode: '+41', flag: '🇨🇭', maxDigits: 9 },
  { code: 'AT', name: 'Áustria', dialCode: '+43', flag: '🇦🇹', maxDigits: 10 },
  { code: 'PL', name: 'Polónia', dialCode: '+48', flag: '🇵🇱', maxDigits: 9 },
  { code: 'AO', name: 'Angola', dialCode: '+244', flag: '🇦🇴', maxDigits: 9 },
  { code: 'MZ', name: 'Moçambique', dialCode: '+258', flag: '🇲🇿', maxDigits: 9 },
  { code: 'CV', name: 'Cabo Verde', dialCode: '+238', flag: '🇨🇻', maxDigits: 7 },
];

const PhoneInput = ({ 
  value = '', 
  onChange, 
  placeholder = 'Número de telefone',
  className = '',
  error = false,
  label = 'Telefone',
  required = false,
  defaultCountry = null // 'PT', 'BR', etc — when set, skips IP detection
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const initialCountry = defaultCountry
    ? (COUNTRIES.find(c => c.code === defaultCountry) || COUNTRIES[0])
    : COUNTRIES[0];
  const [selectedCountry, setSelectedCountry] = useState(initialCountry); // Default: Portugal (or override)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [detectedByIP, setDetectedByIP] = useState(false);

  // Detect country by IP on mount (skip when defaultCountry is provided)
  useEffect(() => {
    if (defaultCountry) return; // Honor explicit default
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data.country_code;
        const detected = COUNTRIES.find(c => c.code === countryCode);
        if (detected && !detectedByIP) {
          setSelectedCountry(detected);
          setDetectedByIP(true);
        }
      } catch (e) {
        // Default to Portugal if detection fails
        if (process.env.NODE_ENV === 'development') console.log('IP detection failed, using default');
      }
    };
    detectCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parse initial value if provided
  useEffect(() => {
    if (value) {
      // Try to extract dial code and number from value
      for (const country of COUNTRIES) {
        if (value.startsWith(country.dialCode)) {
          setSelectedCountry(country);
          setPhoneNumber(value.slice(country.dialCode.length).replace(/\s/g, ''));
          return;
        }
      }
      // If no match, just use the number part
      setPhoneNumber(value.replace(/[^\d]/g, ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/[^\d]/g, ''); // Only digits
    const maxDigits = selectedCountry.maxDigits;
    const trimmed = input.slice(0, maxDigits);
    setPhoneNumber(trimmed);
    
    // Call onChange with full phone number (dial code + number)
    if (onChange) {
      const fullNumber = trimmed ? `${selectedCountry.dialCode}${trimmed}` : '';
      onChange(fullNumber);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    
    // Re-validate phone number with new country's max digits
    const trimmed = phoneNumber.slice(0, country.maxDigits);
    setPhoneNumber(trimmed);
    
    if (onChange && trimmed) {
      onChange(`${country.dialCode}${trimmed}`);
    }
  };

  return (
    <div className="w-full">
      {label !== null && label !== false && label !== '' && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className={`flex border rounded-xl overflow-hidden ${error ? 'border-red-300' : 'border-gray-200'} focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${className}`}>
        {/* Country selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-1 px-3 py-3 bg-gray-50 border-r border-gray-200 hover:bg-gray-100 transition min-w-[90px]"
          >
            <span className="text-lg">{selectedCountry.flag}</span>
            <span className="text-sm font-medium text-gray-600">{selectedCountry.dialCode}</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          
          {showDropdown && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
              {COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition ${
                    selectedCountry.code === country.code ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm text-gray-700 flex-1 text-left">{country.name}</span>
                  <span className="text-sm text-gray-500">{country.dialCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Phone number input */}
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          maxLength={selectedCountry.maxDigits}
          className="flex-1 px-4 py-3 focus:outline-none text-gray-900"
        />
      </div>
      
      {/* Helper text */}
      <p className="mt-1 text-xs text-gray-500">
        {selectedCountry.code === 'PT' ? 'Portugal: 9 dígitos' : 
         selectedCountry.code === 'BR' ? 'Brasil: 10-11 dígitos (com DDD)' : 
         `${selectedCountry.name}: máx. ${selectedCountry.maxDigits} dígitos`}
      </p>
    </div>
  );
};

export default PhoneInput;
